export interface ProgressChartSeriesPoint {
  date: string // 'YYYY-MM-DD'
  value: number
  status?: 'CONFIRMED' | 'SELF_REPORTED'
}

export interface ProgressChartProps {
  series: ProgressChartSeriesPoint[]
  kind: 'line' | 'bar'
  unit?: string
  target?: number | null
  direction?: 'HIGHER_BETTER' | 'LOWER_BETTER'
  height?: number
  ariaLabel: string
  className?: string
  testId?: string
}

export function ProgressChart({
  series,
  kind,
  unit,
  target,
  direction,
  height = 160,
  ariaLabel,
  className,
  testId,
}: ProgressChartProps) {
  void direction
  const viewBoxWidth = 360
  const paddingTop = 20
  const paddingBottom = 28
  const paddingLeft = 32
  const paddingRight = 32
  const plotWidth = viewBoxWidth - paddingLeft - paddingRight
  const plotHeight = Math.max(30, height - paddingTop - paddingBottom)

  // Compute numeric min & max
  const rawValues = series.map((s) => s.value).filter(Number.isFinite)
  const hasTarget = typeof target === 'number' && Number.isFinite(target)

  let minVal = 0
  let maxVal = 1

  if (rawValues.length > 0 || hasTarget) {
    const candidateVals = [...rawValues, ...(hasTarget ? [target as number] : [])]
    const rawMin = Math.min(...candidateVals)
    const rawMax = Math.max(...candidateVals)

    if (rawMin === rawMax) {
      if (rawMin > 0) {
        minVal = 0
        maxVal = rawMin * 1.25 || 1
      } else if (rawMin < 0) {
        minVal = rawMin * 1.25
        maxVal = 0
      } else {
        minVal = 0
        maxVal = 1
      }
    } else {
      const padding = (rawMax - rawMin) * 0.1
      minVal = Math.min(rawMin, rawMin >= 0 && rawMin <= padding ? 0 : rawMin - padding)
      maxVal = rawMax + padding
    }
  }

  const valRange = maxVal - minVal || 1

  const getY = (val: number) => {
    const clamped = Math.max(minVal, Math.min(maxVal, val))
    return paddingTop + plotHeight - ((clamped - minVal) / valRange) * plotHeight
  }

  const getX = (idx: number, total: number) => {
    if (total <= 1) return paddingLeft + plotWidth / 2
    return paddingLeft + (idx / (total - 1)) * plotWidth
  }

  const firstDate = series.length > 0 ? series[0].date : null
  const lastDate = series.length > 0 ? series[series.length - 1].date : null

  return (
    <div className={`progressChart inline-block w-full ${className ?? ''}`} data-testid={testId}>
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${viewBoxWidth} ${height}`}
        width="100%"
        height={height}
        className="overflow-visible select-none"
      >
        {/* Baseline / axis line */}
        <line
          x1={paddingLeft}
          y1={paddingTop + plotHeight}
          x2={viewBoxWidth - paddingRight}
          y2={paddingTop + plotHeight}
          stroke="var(--border-subtle)"
          strokeWidth={1}
        />

        {/* Optional target line */}
        {hasTarget && (
          <g data-testid="progress-chart-target-line">
            <line
              x1={paddingLeft}
              y1={getY(target as number)}
              x2={viewBoxWidth - paddingRight}
              y2={getY(target as number)}
              stroke="var(--border-strong)"
              strokeDasharray="4 4"
              strokeWidth={1.5}
            />
            <text
              x={viewBoxWidth - paddingRight}
              y={Math.max(12, getY(target as number) - 4)}
              textAnchor="end"
              className="text-[10px] font-mono fill-[var(--text-secondary)]"
            >
              Target: {target}
              {unit ? ` ${unit}` : ''}
            </text>
          </g>
        )}

        {/* Line mode rendering */}
        {kind === 'line' && series.length > 0 && (
          <g data-testid="progress-chart-line-group">
            {/* Connecting paths */}
            {series.slice(1).map((curr, idx) => {
              const prev = series[idx]
              const x1 = getX(idx, series.length)
              const y1 = getY(prev.value)
              const x2 = getX(idx + 1, series.length)
              const y2 = getY(curr.value)
              const isDashed = curr.status === 'SELF_REPORTED' || prev.status === 'SELF_REPORTED'

              return (
                <line
                  key={`line-${idx}`}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="var(--color-primary, var(--accent))"
                  strokeWidth={2}
                  strokeDasharray={isDashed ? '4 4' : undefined}
                />
              )
            })}

            {/* Points */}
            {series.map((point, idx) => {
              const cx = getX(idx, series.length)
              const cy = getY(point.value)
              const isSelfReported = point.status === 'SELF_REPORTED'

              return (
                <circle
                  key={`point-${idx}`}
                  cx={cx}
                  cy={cy}
                  r={isSelfReported ? 4.5 : 4}
                  fill={isSelfReported ? 'var(--bg-surface, var(--surface))' : 'var(--color-primary, var(--accent))'}
                  stroke="var(--color-primary, var(--accent))"
                  strokeWidth={isSelfReported ? 2 : 1}
                  data-status={point.status ?? 'CONFIRMED'}
                  data-index={idx}
                />
              )
            })}
          </g>
        )}

        {/* Bar mode rendering */}
        {kind === 'bar' && series.length > 0 && (
          <g data-testid="progress-chart-bar-group">
            {series.map((point, idx) => {
              const barWidth = Math.max(6, Math.min(24, Math.floor(plotWidth / (series.length * 1.6))))
              const xCenter = getX(idx, series.length)
              const barX = xCenter - barWidth / 2
              const barY = getY(point.value)
              const barHeight = Math.max(3, paddingTop + plotHeight - barY)
              const isSelfReported = point.status === 'SELF_REPORTED'

              return (
                <rect
                  key={`bar-${idx}`}
                  x={barX}
                  y={barY}
                  width={barWidth}
                  height={barHeight}
                  rx={2}
                  fill={
                    isSelfReported
                      ? 'color-mix(in srgb, var(--color-primary, var(--accent)) 25%, transparent)'
                      : 'var(--color-primary, var(--accent))'
                  }
                  stroke={isSelfReported ? 'var(--color-primary, var(--accent))' : 'none'}
                  strokeDasharray={isSelfReported ? '3 3' : undefined}
                  strokeWidth={isSelfReported ? 1.5 : 0}
                  data-status={point.status ?? 'CONFIRMED'}
                  data-index={idx}
                />
              )
            })}
          </g>
        )}

        {/* Date labels: first and last dates */}
        {firstDate && (
          <text
            x={paddingLeft}
            y={height - 8}
            textAnchor="start"
            className="text-[11px] font-mono fill-[var(--text-secondary)]"
            data-testid="progress-chart-first-date"
          >
            {firstDate}
          </text>
        )}
        {lastDate && lastDate !== firstDate && (
          <text
            x={viewBoxWidth - paddingRight}
            y={height - 8}
            textAnchor="end"
            className="text-[11px] font-mono fill-[var(--text-secondary)]"
            data-testid="progress-chart-last-date"
          >
            {lastDate}
          </text>
        )}
      </svg>

      {/* Accessible visually hidden table */}
      <table className="sr-only" data-testid="progress-chart-hidden-table">
        <caption>{ariaLabel}</caption>
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">Value</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {series.map((point, idx) => (
            <tr key={`table-row-${idx}`}>
              <td>{point.date}</td>
              <td>
                {point.value}
                {unit ? ` ${unit}` : ''}
              </td>
              <td>{point.status ?? 'CONFIRMED'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
